import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";

type Props = {
  /** Top left: what the row is (date and kind). */
  title: ReactNode;
  /** Top right: the one figure to look at first. */
  headline: ReactNode;
  /** The other facts of the row, one `<div>` each. */
  children: ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  /** Shown under the card while it is expanded. */
  details: ReactNode;
};

/**
 * One row of the settlement lists drawn for a phone. The tables are ten
 * columns wide and would need sideways scrolling to reach the debt, so a phone
 * gets this card instead: title and headline figure on top, the rest below,
 * and the row's expandable details behind a 44 px button.
 */
export default function PhoneRowCard({
  title,
  headline,
  children,
  isExpanded,
  onToggle,
  details,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="border-b border-gray-100 px-3 py-2 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 break-words text-sm font-medium text-gray-900">{title}</div>
        <div className="shrink-0 text-right text-sm font-semibold text-gray-900">{headline}</div>
      </div>
      <div className="mt-1 space-y-0.5 break-words text-sm text-gray-600">{children}</div>
      <button
        type="button"
        aria-expanded={isExpanded}
        className="inline-flex min-h-11 items-center gap-1 text-sm text-indigo-600"
        onClick={onToggle}
      >
        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {isExpanded ? t("collapse") : t("expand")}
      </button>
      {isExpanded && <div className="overflow-x-auto rounded-sm bg-gray-50 p-2">{details}</div>}
    </div>
  );
}
